import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Badge } from '@/components/atoms/Badge';
import {
    createDataTableColumnHelper,
    type DataTableColumnDef,
    timeSortFn,
} from '@/components/organisms/DataTable';
import {
    type AlertAcknowledgement,
    type Alert as AlertEntry,
    useSilenceAlert,
    useUnsilenceAlert,
} from '@/domain/alerts';
import { formatDateTimeWorded } from '@/utils/date';

import { bioscopeAlertCodes } from '../../Dashboard/components/Alert/bioscopeAlertCodes';

import { type BioscopeRow, isAlertAcknowledged } from './bioscopeRows';
import { cmpVersion, parseBioscopeVersion } from './bioscopeVersion';
import { equipmentColumns } from './columns/equipmentColumns';
import { placeColumns } from './columns/placeColumns';
import AlertsContainer from './components/BioscopeAlerts';

/**
 * The bioscope table's columns. Alerts read the acknowledgements to sort silenced ones last and
 * to offer silencing or unsilencing from each alert. `fishGroupsEnabled` is the
 * `fish-group-tracking` flag: off, the table and its links are as before fish groups.
 */
export function useBioscopeTableColumns(
    acknowledgements: AlertAcknowledgement[],
    fishGroupsEnabled: boolean
): DataTableColumnDef<BioscopeRow>[] {
    const { t } = useTranslation();
    const { t: tFishGroups } = useTranslation('fishGroups');

    const alertMap: Record<string, string> = Object.fromEntries(
        bioscopeAlertCodes.map((alertCode): [string, string] => [
            alertCode,
            t(`alert.${alertCode}`),
        ])
    );

    const acknowledgeAlert = useSilenceAlert();
    const unacknowledgeAlert = useUnsilenceAlert();

    const columnHelper = createDataTableColumnHelper<BioscopeRow>();

    return columnHelper.columns([
        columnHelper.accessor((row) => row.id, {
            id: 'id',
            header: t('internal.bioscopeList.columns.id'),
            cell: (info) => (
                <Link to={`/internal/bioscopes/${info.getValue()}`}>{info.getValue()}</Link>
            ),
        }),
        columnHelper.accessor((row) => row.bioscopeStatus, {
            id: 'bioscopeStatus',
            header: t('internal.bioscopeList.columns.bioscopeStatus'),
            cell: (info) => {
                const status = info.getValue();
                return (
                    <Badge tone={status === 'online' ? 'success' : 'warning'} size="md">
                        {status || t('internal.bioscopeList.statusUnknown')}
                    </Badge>
                );
            },
            sortFn: (a, b) => {
                const statusA = a.original.bioscopeStatus || 'unknown';
                const statusB = b.original.bioscopeStatus || 'unknown';
                return statusA.localeCompare(statusB);
            },
        }),
        columnHelper.accessor((row) => row.connection?.cabinet, {
            id: 'cabinet',
            header: t('internal.bioscopeList.columns.cabinet'),
        }),
        columnHelper.accessor((row) => row.bioscopeVersion, {
            id: 'bioscopeVersion',
            header: t('internal.bioscopeList.columns.bioscopeVersion'),
        }),
        columnHelper.accessor((row) => row.version?.version, {
            id: 'version',
            header: t('internal.bioscopeList.columns.version'),
            cell: (info) => parseBioscopeVersion(info.getValue()),
            sortFn: (a, b) =>
                cmpVersion(a.original.version?.version ?? '', b.original.version?.version ?? ''),
        }),
        ...placeColumns(t, { fishGroupsEnabled, tFishGroups }),
        columnHelper.accessor((row) => row.lastLocation?.updatedAt, {
            id: 'moved',
            header: t('internal.bioscopeList.columns.moved'),
            cell: (cell) =>
                cell.row.original?.lastLocation?.updatedAt
                    ? formatDateTimeWorded(cell.row.original.lastLocation?.updatedAt)
                    : null,
            sortFn: timeSortFn,
        }),
        columnHelper.accessor((row) => row.lastMeasurement?.timestamp, {
            id: 'lastMeasurement',
            header: t('internal.bioscopeList.columns.lastMeasurement'),

            enableGlobalFilter: false,
            cell: (cell) =>
                cell.row.original?.lastMeasurement?.timestamp
                    ? formatDateTimeWorded(cell.row.original.lastMeasurement.timestamp)
                    : null,
            // The API sends a null timestamp for a bioscope that has never measured; the
            // table sorts it last whichever way the column sorts.
            sortFn: timeSortFn,
        }),
        columnHelper.accessor(
            (row) => ({
                data: row.alerts,
                acknowledgements,
                toString: () => row.alerts.map((alert) => alertMap[alert.alertCode]).join(', '),
            }),
            {
                id: 'alerts',
                header: t('internal.bioscopeList.columns.alerts'),
                cell: (info) => (
                    <AlertsContainer
                        alerts={info.row.original.alerts}
                        location={info.row.original.lastLocation}
                        alertMap={alertMap}
                        onSilence={(silence) => acknowledgeAlert.mutate(silence)}
                        onUnsilence={(unsilence) => unacknowledgeAlert.mutate(unsilence)}
                    />
                ),

                sortFn: ({ original: a }, { original: b }) => {
                    const filteredA = a.alerts.filter(
                        (alert) => !isAlertAcknowledged(acknowledgements, alert, a.lastLocation?.id)
                    );
                    const filteredB = b.alerts.filter(
                        (alert) => !isAlertAcknowledged(acknowledgements, alert, b.lastLocation?.id)
                    );

                    const minAlert = (cand: AlertEntry[]) =>
                        Math.min(
                            ...cand.map((alert) => bioscopeAlertCodes.indexOf(alert.alertCode))
                        );

                    return (
                        minAlert(filteredB) - minAlert(filteredA) ||
                        filteredA.length - filteredB.length ||
                        0
                    );
                },
            }
        ),
        ...equipmentColumns(t),
    ]);
}
